import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node, mergeAttributes } from '@tiptap/core';
import type { SmsVariable } from '../model/types';

/* ─── Variable inline node ─────────────────────────────────────── */

const VariableNode = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      key:   { default: null },
      label: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-variable': node.attrs.key,
        class: 'sms-var-chip',
      }),
      node.attrs.label,
    ];
  },

  // getText() returns {key} — used for saving to DB
  renderText({ node }) {
    return node.attrs.key as string;
  },
});

/* ─── Helpers ───────────────────────────────────────────────────── */

const parseRawText = (
  text: string,
  varMap: Map<string, string>,
): object => {
  const lines = text.split('\n');
  return {
    type: 'doc',
    content: lines.map(line => {
      const parts = line.split(/(\{[^}]+\})/g).filter(Boolean);
      return {
        type: 'paragraph',
        content: parts.map(part => {
          const label = varMap.get(part);
          if (label) return { type: 'variable', attrs: { key: part, label } };
          return { type: 'text', text: part };
        }),
      };
    }),
  };
};

/* ─── Component ─────────────────────────────────────────────────── */

interface SmsTemplateEditorProps {
  defaultValue: string;
  onChange: (value: string) => void;
  variables: SmsVariable[];
}

const SmsTemplateEditor: React.FC<SmsTemplateEditorProps> = ({ defaultValue, onChange, variables }) => {
  const varMap = new Map(variables.map(v => [v.key, v.label]));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: false, italic: false, strike: false,
        code: false, codeBlock: false, blockquote: false,
        heading: false, bulletList: false, orderedList: false,
        listItem: false, horizontalRule: false,
      }),
      VariableNode,
    ],
    content: parseRawText(defaultValue, varMap),
    onUpdate({ editor }) {
      onChange(editor.getText({ blockSeparator: '\n' }));
    },
    editorProps: {
      attributes: { class: 'sms-editor-area' },
    },
  });

  // sync if defaultValue changes externally (e.g. modal reopened)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getText({ blockSeparator: '\n' });
    if (current !== defaultValue) {
      editor.commands.setContent(parseRawText(defaultValue, varMap), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const insertVariable = (key: string, label: string) => {
    editor?.chain().focus().insertContent({ type: 'variable', attrs: { key, label } }).run();
  };

  return (
    <>
      <style>{`
        .sms-editor-area {
          min-height: 140px;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid var(--color-border, #e5e7eb);
          background: var(--color-surface, #fff);
          font-size: 13px;
          line-height: 1.8;
          outline: none;
          word-break: break-word;
          color: var(--color-text, #111827);
          cursor: text;
        }
        .sms-editor-area p { margin: 0; }
        .tiptap:focus { border-color: var(--color-primary, #6366f1); box-shadow: 0 0 0 3px rgba(99,102,241,.12); }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--color-text-placeholder, #9ca3af);
          pointer-events: none;
          height: 0;
        }
        .sms-var-chip {
          display: inline-flex;
          align-items: center;
          padding: 1px 8px;
          border-radius: 4px;
          background: rgba(99,102,241,.12);
          color: #4f46e5;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          user-select: none;
          cursor: default;
          margin: 0 1px;
        }
      `}</style>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* Editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <EditorContent editor={editor} />
        </div>

        {/* Variables panel */}
        <div style={{
          width: 195, flexShrink: 0,
          border: '1px solid var(--color-border, #e5e7eb)',
          borderRadius: 8, overflow: 'hidden',
        }}>
          <div style={{
            padding: '6px 10px', fontSize: 10, fontWeight: 700,
            color: 'var(--color-text-secondary, #6b7280)', textTransform: 'uppercase',
            letterSpacing: '.05em', background: 'var(--color-bg-subtle, #f9fafb)',
            borderBottom: '1px solid var(--color-border, #e5e7eb)',
          }}>
            Переменные
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {variables.map(v => (
              <button
                key={v.key}
                type="button"
                title={v.key}
                onClick={() => insertVariable(v.key, v.label)}
                style={{
                  display: 'flex', flexDirection: 'column', width: '100%', gap: 1,
                  padding: '6px 10px', border: 'none',
                  borderBottom: '1px solid var(--color-border, #e5e7eb)',
                  background: 'none', cursor: 'pointer', textAlign: 'left',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-hover, #f3f4f6)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 500 }}>{v.label}</span>
                <span style={{ fontSize: 10, color: 'var(--color-text-secondary, #9ca3af)', fontFamily: 'monospace' }}>{v.key}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export { SmsTemplateEditor };
